import type { OfferCreateData, OfferCreatePayload, OfferCreated, OfferDetail, OfferBuyerView, OfferCartResult, OfferListItem, OfferListResponse, OfferSendResult, OfferUpdatePayload, ApiResponse, B2BCartAddManyResponse, B2BCartResponse, BannerItem, Menus, BlogArticleDetail, BlogArticlesResponse, BlogCategoriesResponse, ChangePasswordPayload, ChangePasswordResponse, CMSPage, CustomerProfile, CustomerAddress, CustomerAddressListResponse, CustomerAddressPayload, B2BOrderListResponse, B2BOrderDetail, MessageResponse, NewsListingResponse, CatalogData, CatalogResult, CategoryCollection, CollectionProduct, ContactFormData, ProductModification, FaqListResponse, ProductListQuery, SearchSuggestResult, StoreSettings, Project, NewsletterSubscriptionResult, NewsletterUnsubscribeResult, RegisterPayload, RegisterResponse, SavedCatalogFilter, GuideListResponse, GuideDetail, InfoblockListResponse, B2BCheckoutOptions, B2BCheckoutState, B2BCheckoutDeliveryPayload, B2BCheckoutPaymentPayload, B2BCheckoutSubmitted } from '~/types';

export function createApiService() {
const { $i18n, $fetchWithToken } = useNuxtApp()

return {
    getCategories(id: string | null) {
        if (id) {
            return $fetchWithToken<ApiResponse>(`/api/${$i18n.locale.value}/categories/list/${id}/`);
        }

        return $fetchWithToken<ApiResponse>(`/api/${$i18n.locale.value}/categories/list/`);
    },
    getCategory(id: string | number) {
        return $fetchWithToken<ApiResponse>(`/api/${$i18n.locale.value}/categories/single/${id}/`);
    },
    /**
     * One of only two things still served from the SHARED `/cache/**` layer (the
     * other is `project/*`): the backend keys `menu/all` on {project_id, locale}
     * alone — no price, no discount group, no customer reference — so a single
     * entry is safe for every customer. It still needs a Bearer token, which the
     * cache route forwards upstream on a miss.
     */
    getMenus() {
        return $fetchWithToken<ApiResponse<Menus>>(`/cache/api/${$i18n.locale.value}/menu/all/`);
    },
    /**
     * Instant search behind the header sheet: text suggestions + a short product
     * rail in one round trip, which is what the design draws.
     *
     * Deliberately on the uncached `/api/**` proxy, not `/cache/**`: B2B prices
     * are customer-group specific, so a shared SWR entry would serve one
     * customer's prices to every other one. It also keeps the cache key space
     * from growing by one entry per keystroke.
     */
    searchSuggest(search: string, limit?: number, signal?: AbortSignal) {
        return $fetchWithToken<ApiResponse<SearchSuggestResult>>(`/api/${$i18n.locale.value}/search/suggest/`, {
            method: 'GET',
            query: {
                q: search,
                limit
            },
            signal
        });
    },
    getSettings() {
        return $fetchWithToken<ApiResponse<StoreSettings>>(`/cache/api/${$i18n.locale.value}/project/settings/`);
    },
    getProjectList() {
        return $fetchWithToken<ApiResponse<Project[]>>(`/cache/api/${$i18n.locale.value}/project/list/`);
    },
    getInfoblockByAlias(alias: string) {
        return $fetchWithToken<ApiResponse<InfoblockListResponse>>(`/api/${$i18n.locale.value}/infoblocks/by-alias/${alias}/`);
    },
    subscribeNewsletter(email: string, subscription_source: string) {
        return $fetchWithToken<ApiResponse<NewsletterSubscriptionResult>>(`/api/${$i18n.locale.value}/subscribers/subscribe/`, {
            method: 'POST',
            body: { email, subscription_source }
        });
    },
    unsubscribeNewsletter(email: string, nlhash: string) {
        return $fetchWithToken<ApiResponse<NewsletterUnsubscribeResult>>(`/api/${$i18n.locale.value}/subscribers/unsubscribe/`, {
            method: 'POST',
            body: { email, nlhash }
        });
    },
    /**
     * NOT cached: a CMS page's `collection` sections are hydrated server-side with
     * full product cards, so both the prices and the set of products/sections vary
     * by the caller's discount group. A shared cache entry here would hand one
     * customer's price list to another.
     */
    getPageByType(type: string) {
        return $fetchWithToken<ApiResponse<CMSPage>>(`/api/${$i18n.locale.value}/pages/by_type/${type}/`);
    },
    getPageByPath(path: string) {
        const normalizedPath = path.replace(/^\/+|\/+$/g, '');
        if (!normalizedPath) {
            return Promise.resolve({ success: false, data: null, message: 'Missing page path' } as unknown as ApiResponse<CMSPage>);
        }
        return $fetchWithToken<ApiResponse<CMSPage>>(`/api/${$i18n.locale.value}/pages/single/${normalizedPath}/`);
    },
    getBlogCategories() {
        return $fetchWithToken<ApiResponse<BlogCategoriesResponse>>(`/api/${$i18n.locale.value}/news/categories/`);
    },
    getBlogArticles(query: { parent_page_id?: string; page_id?: string; product_category_id?: string; page?: number; pageSize?: number }) {
        return $fetchWithToken<ApiResponse<BlogArticlesResponse>>(`/api/${$i18n.locale.value}/news/list/`, { query });
    },
    getBlogArticle(slug: string) {
        return $fetchWithToken<ApiResponse<BlogArticleDetail>>(`/api/${$i18n.locale.value}/news/single/${slug}/`);
    },
    getFaqList(query?: { product_category_id?: string; show_in_help?: number }) {
        return $fetchWithToken<ApiResponse<FaqListResponse>>(`/api/${$i18n.locale.value}/faq/list/`, { query });
    },
    getBannersByType(type: string | number) {
        return $fetchWithToken<ApiResponse<BannerItem[]>>(`/api/${$i18n.locale.value}/banners/by_type/${type}/`);
    },
    getCategoryCollections(categoryId: string | number) {
        return $fetchWithToken<ApiResponse<CategoryCollection[]>>(`/api/${$i18n.locale.value}/collections/category-list/`, {
            query: { category: categoryId },
        })
    },
    // skipProducts drops the embedded page-1 listing (initial_products) from the
    // response. Only the bare catalog url can use it; a paginated/filtered view
    // fetches its own grid, so asking for it there costs a full product build
    // backend-side and ~43KB of dead payload in __NUXT_DATA__.
    // `selection` carries the product page's ?item= / ?modification= through:
    // the same endpoint answers both a listing url and a product url, and on a
    // product url that argument is what decides which colour — and therefore
    // which price tiers — the payload describes. It is ignored by the listing
    // branches, so it costs nothing to send.
    getCatalogPage(path : string, skipProducts = false, selection?: Record<string, string>) {
        path = path.replace(/^\/+|\/+$/g, '');

        const query = {
            ...(skipProducts ? { skip_products: 1 } : {}),
            ...(selection ?? {}),
        };

        return $fetchWithToken<ApiResponse<CatalogData>>(
            `/api/${$i18n.locale.value}/products/component/${path}/`,
            Object.keys(query).length ? { query } : undefined,
        );
    },
    // The product page's similar/related rail. Separate from the detail call so
    // three collection passes never hold the page itself up.
    getSimilarProducts(path: string, limit?: number) {
        path = path.replace(/^\/+|\/+$/g, '');

        return $fetchWithToken<ApiResponse<CollectionProduct[]>>(
            `/api/${$i18n.locale.value}/products/similar/${path}/`,
            limit ? { query: { limit } } : undefined,
        );
    },
    getProducts(query: ProductListQuery) {
        return $fetchWithToken<ApiResponse<CatalogResult>>(`/api/${$i18n.locale.value}/products/list/`, {
            query
        });
    },
    getEditorBlockProducts(id: number | string) {
        return $fetchWithToken<ApiResponse<CollectionProduct[]>>(
            `/api/${$i18n.locale.value}/products/editor-block-products/${id}/`
        )
    },
    getRecommendedByItem(itemId: string) {
        return $fetchWithToken<ApiResponse<CollectionProduct[]>>(
            `/api/${$i18n.locale.value}/products/recommended-by-item/${itemId}/`
        )
    },
    getRecommendedModifications(modificationId: string) {
        return $fetchWithToken<ApiResponse<ProductModification[]>>(
            `/api/${$i18n.locale.value}/products/recommended-modifications/${modificationId}/`
        )
    },
    submitContactForm(data: ContactFormData) {
        return $fetchWithToken<ApiResponse<MessageResponse>>(`/api/${$i18n.locale.value}/contact/send/`, {
            method: 'POST',
            body: data
        });
    },
    getMe() {
        return $fetchWithToken<ApiResponse<CustomerProfile>>(`/api/${$i18n.locale.value}/customers/me/`);
    },
    getNewsListing(pageId: string | number) {
        return $fetchWithToken<ApiResponse<NewsListingResponse>>(`/api/${$i18n.locale.value}/news/listing/${pageId}/`);
    },
    getGuidesList() {
        return $fetchWithToken<ApiResponse<GuideListResponse>>(`/api/${$i18n.locale.value}/guides/list/`);
    },
    getGuide(slug: string) {
        return $fetchWithToken<ApiResponse<GuideDetail>>(`/api/${$i18n.locale.value}/guides/single/${slug}/`);
    },
    /**
     * The B2B cart (`src/Trivian/Cart.php` on nostra-b2b).
     *
     * Nothing here takes a cart hash: a B2B customer is always signed in, so the
     * backend resolves the cart from the Bearer token's customer (`e_carts.customer_id`
     * with `step < 5`) and the inherited B2C cookie plays no part. A line is addressed
     * by `item_id` + `min_qty` — the purchase unit, not just the SKU — and every call
     * answers with the whole cart, so the "added to cart" modal needs no second request.
     */
    getB2BCart() {
        return $fetchWithToken<ApiResponse<B2BCartResponse>>(
            `/api/${$i18n.locale.value}/cart/info/`
        );
    },
    /** Adds to whatever that line already holds. */
    addB2BCartItem(itemId: string, minQty: number, amount: number) {
        return $fetchWithToken<ApiResponse<B2BCartResponse>>(
            `/api/${$i18n.locale.value}/cart/add/`,
            { method: 'POST', body: { item_id: itemId, min_qty: minQty, amount } }
        );
    },
    /** Sets the line to `amount` purchase units; 0 removes it. */
    updateB2BCartItem(itemId: string, minQty: number, amount: number) {
        return $fetchWithToken<ApiResponse<B2BCartResponse>>(
            `/api/${$i18n.locale.value}/cart/update/`,
            { method: 'POST', body: { item_id: itemId, min_qty: minQty, amount } }
        );
    },
    /**
     * Adds several purchase units in one round trip — the manager toolbar's
     * "Tęsti" button, which empties a whole page of ticked rows into the cart.
     *
     * Deliberately not N calls to `cart/add`: each of those returns the whole
     * cart, so a 40-row selection would ship the cart back 40 times and leave the
     * header counter flickering through every intermediate total. The batch
     * endpoint validates per item, so it also reports which rows were refused
     * instead of failing the lot.
     */
    addB2BCartItems(items: { itemId: string; minQty: number; amount: number }[]) {
        return $fetchWithToken<ApiResponse<B2BCartAddManyResponse>>(
            `/api/${$i18n.locale.value}/cart/add-many/`,
            {
                method: 'POST',
                body: {
                    items: items.map(item => ({
                        item_id: item.itemId,
                        min_qty: item.minQty,
                        amount: item.amount,
                    })),
                },
            }
        );
    },
    /**
     * The cart as a PDF — the design's "Formuoti PDF" button.
     *
     * Streams raw bytes rather than answering with a URL, and deliberately so: the
     * endpoint is Bearer-only, and a plain `<a href>` navigation carries no
     * Authorization header, so a link would be unreachable. The backend also deletes
     * the rendered file straight after streaming it, because `files/` is web-reachable
     * and a cart PDF carries that customer's group prices.
     *
     * `Accept` overrides the wrapper's `application/json` default; it is spread after
     * the base headers, so this wins.
     */
    getB2BCartPdf() {
        return $fetchWithToken<Blob>(
            `/api/${$i18n.locale.value}/cart/pdf/`,
            { responseType: 'blob', headers: { Accept: 'application/pdf' } }
        );
    },
    /**
     * Everything the offer-creation page renders: the manager's cart already
     * mapped to offer lines, the customers they may address it to, and the
     * pricing options. Manager-only — 403 for anyone else.
     */
    getOfferCreateData() {
        return $fetchWithToken<ApiResponse<OfferCreateData>>(
            `/api/${$i18n.locale.value}/offers/create/`
        );
    },
    /**
     * Creates the offer. Omitting `items` uses the whole cart, which is what the
     * Smarty page does; supplying them restricts to lines already in the cart.
     * The cart is deliberately NOT emptied — a manager may quote the same basket
     * to more than one customer.
     */
    /** The manager's own offers, newest first. */
    /**
     * The manager's own offers. Manager-gated backend-side (403 otherwise), which
     * is why `AccountMenu` only shows the entry for `is_manager` customers.
     */
    getOffers(query: { page?: number; pageSize?: number } = {}) {
        return $fetchWithToken<ApiResponse<OfferListResponse>>(
            `/api/${$i18n.locale.value}/offers/list/`,
            { query }
        );
    },
    /**
     * One offer, addressed by its NUMBER ("P-20260916004") — what the list links
     * to and what the detail route carries — or by its id.
     *
     * Feeds both the detail page and the edit form: they draw different things
     * out of the same payload. `recipients` only comes back for an offer that is
     * still editable.
     */
    getOffer(key: string | number) {
        return $fetchWithToken<ApiResponse<OfferDetail>>(
            `/api/${$i18n.locale.value}/offers/view/${encodeURIComponent(String(key))}/`
        );
    },
    /**
     * Saves an edited draft. Only status 1 is editable — a sent offer answers
     * 400 — and every `item_id` must already be on the offer.
     */
    updateOffer(id: number, payload: OfferUpdatePayload) {
        return $fetchWithToken<ApiResponse<OfferDetail>>(
            `/api/${$i18n.locale.value}/offers/update/${id}/`,
            { method: 'POST', body: payload }
        );
    },
    /**
     * The buyer's view of an offer, addressed by the per-recipient hash from the
     * offer email. Public: the hash itself is the credential for reading, so this
     * works without a session (`security-api-gate.ts` lets exactly this GET
     * through). `isRecipient` says whether the caller may also order it.
     */
    getOfferByHash(hash: string) {
        return $fetchWithToken<ApiResponse<OfferBuyerView>>(
            `/api/${$i18n.locale.value}/offers/by-hash/${encodeURIComponent(hash)}/`
        );
    },
    /**
     * "Formuoti užsakymą" — replaces the customer's cart with the offer's lines
     * and pins it to that offer. Replaces rather than merges: a cart carrying
     * `offer_id` may only hold that offer's items.
     */
    createOfferCart(hash: string) {
        return $fetchWithToken<ApiResponse<OfferCartResult>>(
            `/api/${$i18n.locale.value}/offers/create-cart/`,
            { method: 'POST', body: { hash } }
        );
    },
    /** Deletes a draft offer (status 1 only, manager-only, own rows only). */
    removeOffer(id: number) {
        return $fetchWithToken<ApiResponse<{ deleted: boolean; id: number; number: string }>>(
            `/api/${$i18n.locale.value}/offers/remove/`,
            { method: 'POST', body: { id } }
        );
    },
    /** Duplicates a sent offer (status 3 only) into a fresh draft. */
    copyOffer(id: number) {
        return $fetchWithToken<ApiResponse<OfferListItem & { copiedFrom: number }>>(
            `/api/${$i18n.locale.value}/offers/copy/`,
            { method: 'POST', body: { id } }
        );
    },

    /**
     * Address book (`e_customer_addresses`). Every call is scoped to the signed-in
     * customer backend-side; `delivery_country` is never sent, the backend forces
     * it from the account's billing country.
     */
    getAddresses() {
        return $fetchWithToken<ApiResponse<CustomerAddressListResponse>>(
            `/api/${$i18n.locale.value}/addresses/list/`
        );
    },
    /** Creates when `payload.id` is absent, updates when it is present. */
    saveAddress(payload: CustomerAddressPayload) {
        return $fetchWithToken<ApiResponse<CustomerAddress>>(
            `/api/${$i18n.locale.value}/addresses/save/`,
            { method: 'POST', body: payload }
        );
    },
    deleteAddress(id: number) {
        return $fetchWithToken<ApiResponse<{ deleted: boolean; id: number }>>(
            `/api/${$i18n.locale.value}/addresses/delete/`,
            { method: 'POST', body: { id } }
        );
    },
    /**
     * Mails the offer, one rendering per recipient. Refuses an offer with no
     * recipients (422) rather than reporting a send that never happened.
     */
    sendOffer(id: number) {
        return $fetchWithToken<ApiResponse<OfferSendResult>>(
            `/api/${$i18n.locale.value}/offers/send/${id}/`,
            { method: 'POST' }
        );
    },
    /** The offer PDF. Bearer-only, so it cannot be an <a href> — same as the cart's. */
    getOfferPdf(id: number) {
        return $fetchWithToken<Blob>(
            `/api/${$i18n.locale.value}/offers/pdf/${id}/`,
            { responseType: 'blob', headers: { Accept: 'application/pdf' } }
        );
    },
    createOffer(payload: OfferCreatePayload) {
        return $fetchWithToken<ApiResponse<OfferCreated>>(
            `/api/${$i18n.locale.value}/offers/create/`,
            { method: 'POST', body: payload }
        );
    },
    removeB2BCartItem(itemId: string, minQty: number) {
        return $fetchWithToken<ApiResponse<B2BCartResponse>>(
            `/api/${$i18n.locale.value}/cart/remove/`,
            { method: 'POST', body: { item_id: itemId, min_qty: minQty } }
        );
    },
    /**
     * Empties the whole cart in one call, and with it releases any offer scope
     * `e_carts.offer_id` holds. Idempotent: an already-empty cart answers 200
     * with the empty cart rather than an error.
     */
    clearB2BCart() {
        return $fetchWithToken<ApiResponse<B2BCartResponse>>(
            `/api/${$i18n.locale.value}/cart/clear/`,
            { method: 'POST' }
        );
    },
    /**
     * Saved catalogue filters (`e_customer_filters` on nostra-b2b).
     *
     * Reading is open to any signed-in customer — the list mixes the customer's
     * own rows with the Nostra-wide `is_global` presets — while saving and
     * deleting are manager-only, which is how the Smarty catalogue gated them.
     */
    getSavedFilters() {
        return $fetchWithToken<ApiResponse<{ filters: SavedCatalogFilter[] }>>(
            `/api/${$i18n.locale.value}/customer-filters/list/`
        );
    },
    /** `url` must be site-relative (`/katalogas/...?filter=...`), max 255 chars. */
    saveCatalogFilter(name: string, url: string) {
        return $fetchWithToken<ApiResponse<{ filter: SavedCatalogFilter }>>(
            `/api/${$i18n.locale.value}/customer-filters/save/`,
            { method: 'POST', body: { name, url } }
        );
    },
    deleteSavedFilter(id: number) {
        return $fetchWithToken<ApiResponse<{ deleted: boolean }>>(
            `/api/${$i18n.locale.value}/customer-filters/delete/`,
            { method: 'POST', body: { id } }
        );
    },
    changePassword(data: ChangePasswordPayload) {
        return $fetchWithToken<ApiResponse<ChangePasswordResponse>>(`/api/${$i18n.locale.value}/customer/change-password/`, {
            method: 'POST',
            body: data
        });
    },
    getProfile(options: Record<string, any> = {}) {
        return $fetchWithToken<ApiResponse<{ customer: CustomerProfile }>>(
            `/api/${$i18n.locale.value}/customer/info/`,
            options
        );
    },
    register(data: RegisterPayload) {
        return $fetchWithToken<RegisterResponse>(`/api/${$i18n.locale.value}/auth/register/`, {
            method: 'POST',
            body: data,
        });
    },
    /**
     * The B2B orders list. Its own shape (`elements` + camelCase), not the
     * inherited B2C `OrderListResponse` — see `B2BOrderListResponse`.
     */
    getOrders(query: { page?: number; pageSize?: number } = {}) {
        return $fetchWithToken<ApiResponse<B2BOrderListResponse>>(
            `/api/${$i18n.locale.value}/orders/list/`,
            { query }
        );
    },
    /** Refills the cart from a past order; same result shape as `cart/add-many`. */
    repeatOrder(number: string) {
        return $fetchWithToken<ApiResponse<B2BCartAddManyResponse>>(
            `/api/${$i18n.locale.value}/orders/repeat/`,
            { method: 'POST', body: { number } }
        );
    },
    /**
     * One B2B order in full, for /paskyra/uzsakymai/{number}/.
     *
     * Keyed by the order NUMBER — the endpoint does not accept an id, because a
     * B2B order number is all digits ("202602160001") and could not be told
     * apart from one. Its own shape (`B2BOrderDetail`, camelCase), not the
     * inherited B2C `Order`.
     */
    getOrderDetail(orderNumber: string) {
        return $fetchWithToken<ApiResponse<B2BOrderDetail>>(
            `/api/${$i18n.locale.value}/orders/view/${encodeURIComponent(orderNumber)}/`
        );
    },
    /**
     * The invoice PDF Business Central raised for an order — the design's
     * "Atsisiųsti sąskaitą".
     *
     * Streams bytes rather than answering with a URL, and deliberately so: the
     * endpoint is Bearer-only, and a plain `<a href>` navigation carries no
     * Authorization header. The file also exists at a guessable
     * `files/bills/invoice_{number}.pdf` on the backend host, so linking there
     * directly would publish one customer's invoice to anyone who can count.
     *
     * Only call this when `B2BOrderDetail.invoice` is non-null; it 404s otherwise.
     */
    getOrderInvoice(orderNumber: string) {
        return $fetchWithToken<Blob>(
            `/api/${$i18n.locale.value}/orders/invoice/${encodeURIComponent(orderNumber)}/`,
            { responseType: 'blob', headers: { Accept: 'application/pdf' } }
        );
    },
    /**
     * Everything the checkout needs in one call: the delivery and payment options,
     * the buyer's address book, what the cart already holds for each of them, and
     * the money — including the VAT split the confirm step draws.
     *
     * One endpoint rather than the B2C `cart/delivery-methods` + `cart/payments`
     * pair because B2B has two delivery methods and one payment method: a second
     * round trip would cost more than the payload it saves. Every step of the flow
     * calls this, which is also what re-hydrates the form on a reload or a step back.
     */
    getCheckoutOptions() {
        return $fetchWithToken<ApiResponse<B2BCheckoutOptions>>(
            `/api/${$i18n.locale.value}/checkout/options/`
        );
    },
    /**
     * Step 1. Stores the delivery method and, for a method that needs one, the
     * address — either copied from an address-book row (`addressId`) or typed in.
     *
     * `deliveryCountry` is deliberately not in the payload: the backend forces it
     * from the account's billing country, exactly as `addresses/save` does.
     */
    saveCheckoutDelivery(payload: B2BCheckoutDeliveryPayload) {
        return $fetchWithToken<ApiResponse<B2BCheckoutState>>(
            `/api/${$i18n.locale.value}/checkout/delivery/`,
            { method: 'POST', body: payload }
        );
    },
    /** Step 2. One method exists ("Pavedimas"), so this is effectively a confirmation. */
    saveCheckoutPayment(payload: B2BCheckoutPaymentPayload) {
        return $fetchWithToken<ApiResponse<B2BCheckoutState>>(
            `/api/${$i18n.locale.value}/checkout/payment/`,
            { method: 'POST', body: payload }
        );
    },
    /**
     * Step 3. Turns the cart into an order and empties it.
     *
     * Answers with the order NUMBER and nothing else — there is no gateway
     * redirect on B2B, unlike the B2C `cart/confirm` this replaces. The caller
     * navigates to the success page and reads the order back through
     * `orders/view`.
     */
    submitCheckout() {
        return $fetchWithToken<ApiResponse<B2BCheckoutSubmitted>>(
            `/api/${$i18n.locale.value}/checkout/submit/`,
            { method: 'POST', body: {} }
        );
    },
    remindPassword(email: string) {
        return $fetchWithToken<ApiResponse<MessageResponse>>(`/api/${$i18n.locale.value}/auth/remind-password/`, {
            method: 'POST',
            body: { email },
        });
    },
    checkCustomerEmail(email: string) {
        return $fetchWithToken<ApiResponse<{ found: boolean }>>(`/api/${$i18n.locale.value}/customer/check-email/`, {
            method: 'POST',
            body: { email },
        });
    },
    validateRemindHash(remind_hash: string) {
        return $fetchWithToken<ApiResponse<Record<string, never>>>(`/api/${$i18n.locale.value}/auth/validate-remind-hash/`, {
            method: 'POST',
            body: { remind_hash },
        });
    },
    resetPassword(remind_hash: string, password: string, password_repeat: string) {
        return $fetchWithToken<ApiResponse>(`/api/${$i18n.locale.value}/auth/reset-password/`, {
            method: 'POST',
            body: { remind_hash, password, password_repeat },
        });
    },
  }
}
